/**
 * Diagram Assistant API Service
 * 
 * This service handles communication with the Diagram Assistant backend API.
 */

import { SYSTEM_MESSAGES, DIAGRAM_TYPES } from '../utils/templates';
import { useApiKey } from '../contexts/ApiKeyContext';

// API endpoint configuration
const API_CONFIG = {
  baseUrl: 'https://mermaid-diagram-assistant.njannasch.workers.dev',
  endpoints: {
    chat: '/api/chat',
    health: '/api/health',
    personalizedPrompts: '/api/personalized-prompts',
    research: '/api/research'
  }
};


// Create a function to get the API key and clearing function
// This is needed because we can't use hooks directly in this service file
let apiKeyClearFunction = null;
let getApiKeyFunction = null;

export function setApiKeyClearFunction(clearFn) {
  apiKeyClearFunction = clearFn;
}

export function setApiKeyGetFunction(getFn) {
  getApiKeyFunction = getFn;
}

function clearApiKeyIfUnauthorized(status) {
  if (status === 401 && apiKeyClearFunction) {
    console.error('API returned 401 Unauthorized, clearing API key');
    apiKeyClearFunction();
    return true;
  }
  return false;
}

// Function to get the headers with Authorization
function getAuthHeaders() {
  const headers = {
    'Content-Type': 'application/json',
  };
  
  if (getApiKeyFunction) {
    const apiKey = getApiKeyFunction();
    if (apiKey) {
      headers['Authorization'] = `Bearer ${apiKey}`;
    }
  }
  
  return headers;
}

// Maximum number of chat history messages to include in requests
const MAX_HISTORY_MESSAGES = 5;

/**
 * Escape special characters in Mermaid text content
 * 
 * @param {string} text - Text content that might contain special characters
 * @returns {string} - Text with special characters escaped
 */
export function escapeMermaidText(text) {
  if (!text) return text;
  
  // Escape parentheses with backslashes
  return text
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)')
    .replace(/\[/g, '\\[')
    .replace(/\]/g, '\\]')
    .replace(/\{/g, '\\{')
    .replace(/\}/g, '\\}');
}

/**
 * Process Mermaid code to escape special characters in text content
 * and remove markdown code block markers
 * 
 * @param {string} code - Mermaid diagram code
 * @returns {string} - Processed Mermaid code with escaped text and without markdown markers
 */
export function processMermaidCode(code) {
  if (!code) return code;
  
  // Remove markdown code block markers
  let processedCode = code;
  
  // Remove ```mermaid at the beginning
  if (processedCode.trim().startsWith('```mermaid')) {
    processedCode = processedCode.replace(/```mermaid/, '');
  }
  
  // Remove ``` at the beginning if it exists (without mermaid keyword)
  if (processedCode.trim().startsWith('```')) {
    processedCode = processedCode.replace(/```/, '');
  }
  
  // Remove ``` at the end if it exists
  if (processedCode.trim().endsWith('```')) {
    processedCode = processedCode.replace(/```\s*$/, '');
  }
  
  // Trim any extra whitespace
  processedCode = processedCode.trim();
  
  // Fix common diagram type casing issues
  processedCode = processedCode.replace(/^mindMap\b/i, 'mindmap');
  processedCode = processedCode.replace(/^flowChart\b/i, 'flowchart');
  processedCode = processedCode.replace(/^sequenceDiagram\b/i, 'sequenceDiagram');
  processedCode = processedCode.replace(/^classDiagram\b/i, 'classDiagram');
  processedCode = processedCode.replace(/^stateDiagram\b/i, 'stateDiagram');
  processedCode = processedCode.replace(/^erDiagram\b/i, 'erDiagram');
  processedCode = processedCode.replace(/^gantt\b/i, 'gantt');
  processedCode = processedCode.replace(/^pie\b/i, 'pie');
  processedCode = processedCode.replace(/^journey\b/i, 'journey');
  processedCode = processedCode.replace(/^quadrantChart\b/i, 'quadrantChart');
  processedCode = processedCode.replace(/^kanban\b/i, 'kanban');
  
  // Split the code into lines
  const lines = processedCode.split('\n');
  
  // Process each line
  const processedLines = lines.map(line => {
    // Skip lines that are purely structural (no text content)
    if (line.trim().startsWith('---') || 
        line.trim() === '' || 
        /^[\s\t]*$/.test(line)) {
      return line;
    }
    
    // Handle node definitions with labels in quotes
    if (line.includes('"')) {
      return line.replace(/"([^"]+)"/g, (match, p1) => {
        return `"${escapeMermaidText(p1)}"`;
      });
    }
    
    // Handle node definitions with labels in brackets
    if (line.includes('[')) {
      return line.replace(/\[([^\]]+)\]/g, (match, p1) => {
        return `[${escapeMermaidText(p1)}]`;
      });
    }
    
    // Handle edge labels
    if (line.includes('|')) {
      return line.replace(/\|([^|]+)\|/g, (match, p1) => {
        return `|${escapeMermaidText(p1)}|`;
      });
    }
    
    return line;
  });
  
  return processedLines.join('\n');
}

/**
 * Send a message to the Diagram Assistant API
 * 
 * @param {Array} messages - Array of message objects with role and content
 * @param {String} currentCode - Current Mermaid diagram code (if any)
 * @param {Object} options - Optional parameters (model, temperature, maxTokens, provider, projectId, locationId)
 * @returns {Promise} - Promise resolving to the API response with message and mermaid_code
 */
export async function sendChatMessage(messages, currentCode = null, options = {}) {
  const defaultOptions = {
    model: 'gpt-4o',
    temperature: 0.7,
    maxTokens: 2048,
    provider: 'openai'
  };
  
  const requestOptions = { ...defaultOptions, ...options };
  
  // Limit history to the last MAX_HISTORY_MESSAGES messages
  const limitedMessages = messages.slice(-MAX_HISTORY_MESSAGES);
  
  // If there's current code, add it as system message at the beginning
  const messagesWithContext = [...limitedMessages];
  if (currentCode) {
    messagesWithContext.unshift({
      role: 'system',
      content: SYSTEM_MESSAGES.diagramContext(currentCode)
    });
  }
  
  try {
    const response = await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.chat}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        messages: messagesWithContext,
        model: requestOptions.model,
        temperature: requestOptions.temperature,
        max_tokens: requestOptions.maxTokens,
        provider: requestOptions.provider,
        project_id: requestOptions.projectId,
        location_id: requestOptions.locationId
      }),
    });
    
    if (!response.ok) {
      // Check for unauthorized response
      if (clearApiKeyIfUnauthorized(response.status)) {
        throw new Error('API key unauthorized. Please enter a valid API key.');
      }
      
      const errorData = await response.json();
      throw new Error(`API error: ${errorData.detail || response.statusText}`);
    }
    
    // Parse the response which should contain message and mermaid_code
    const data = await response.json();
    
    // Ensure the response has the expected structure
    if (!data.message) {
      throw new Error('Invalid response format: missing message');
    }
    
    // Process the mermaid code to escape special characters if present
    if (data.mermaid_code && data.mermaid_code.code) {
      data.mermaid_code.code = processMermaidCode(data.mermaid_code.code);
    }
    
    return {
      message: data.message,
      mermaid_code: data.mermaid_code || null,
      timestamp: data.timestamp
    };
  } catch (error) {
    console.error('Error sending message to Diagram Assistant:', error);
    throw error;
  }
}

/**
 * Check if the API is healthy
 * 
 * @returns {Promise<boolean>} - Promise resolving to true if API is healthy
 */
export async function checkApiHealth() {
  try {
    const response = await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.health}`);
    if (!response.ok) {
      return false;
    }
    const data = await response.json();
    return data.status === 'healthy';
  } catch (error) {
    console.error('API health check failed:', error);
    return false;
  }
}

/**
 * Extract Mermaid code from a message
 * 
 * @param {string} message - Message that might contain Mermaid code
 * @returns {string|null} - Extracted Mermaid code or null if none found
 */
export function extractMermaidCode(message) {
  if (!message) return null;
  
  // Look for code blocks with mermaid tag
  if (message.includes("```mermaid")) {
    const parts = message.split("```mermaid");
    if (parts.length > 1) {
      // Find the closing ``` after the mermaid code
      const codeWithClosing = parts[1].split("```");
      if (codeWithClosing.length > 0) {
        return codeWithClosing[0].trim();
      }
    }
  }
  
  // Look for code blocks without mermaid tag but with valid mermaid syntax
  if (message.includes("```")) {
    const parts = message.split("```");
    for (let i = 1; i < parts.length; i += 2) {
      // Only check odd-indexed parts (inside code blocks)
      if (i % 2 === 1) {
        const code = parts[i].trim();
        if (code && isValidMermaidCode(code)) {
          return code;
        }
      }
    }
  }
  
  // As a fallback, check if the entire message is valid mermaid code
  if (isValidMermaidCode(message)) {
    return message;
  }
  
  return null;
}

/**
 * Check if a string is valid Mermaid code
 * 
 * @param {string} code - Code to check
 * @returns {boolean} - True if the code appears to be valid Mermaid syntax
 */
function isValidMermaidCode(code) {
  if (!code) return false;
  
  const validPrefixes = [
    "graph", "flowchart", "sequenceDiagram", "classDiagram", 
    "erDiagram", "gantt", "pie", "stateDiagram", "journey", "mindmap", "quadrantChart", "kanban",
    // Add common case variations
    "flowChart", "mindMap", "stateDiagram", "sequenceDiagram", "classDiagram", "erDiagram"
  ];
  
  const codeLC = code.trim().toLowerCase();
  
  return validPrefixes.some(prefix => {
    const prefixLC = prefix.toLowerCase();
    return codeLC.startsWith(prefixLC);
  });
}


/**
 * Get personalized prompts based on user persona
 * 
 * @param {Object} persona - User persona object
 * @param {string} persona.job_title - User's job title
 * @param {string} persona.industry - User's industry
 * @param {string} persona.experience_level - User's experience level
 * @param {string} [persona.use_case] - User's primary use case
 * @returns {Promise<Object>} - Promise resolving to personalized prompts response
 */
export async function getPersonalizedPrompts(persona) {
  try {
    const response = await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.personalizedPrompts}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(persona),
    });

    if (!response.ok) {
      // Check for unauthorized response
      if (clearApiKeyIfUnauthorized(response.status)) {
        throw new Error('API key unauthorized. Please enter a valid API key.');
      }
      
      const errorData = await response.json();
      throw new Error(`API request failed: ${errorData.detail || response.statusText}`);
    }

    const data = await response.json();
    return {
      prompts: data.prompts,
      persona_summary: data.persona_summary,
      timestamp: data.timestamp
    };
  } catch (error) {
    console.error('Error fetching personalized prompts:', error);
    throw error;
  }
}

/**
 * Send a research request to the Diagram Assistant API
 * 
 * @param {string} description - The description of the research topic
 * @param {Object} options - Optional parameters (temperature, maxTokens)
 * @returns {Promise} - Promise resolving to the API response with result and timestamp
 */
export async function sendResearchRequest(description, options = {}) {
  const defaultOptions = {
    temperature: 0.8,
    maxTokens: 8192,
    provider: 'gemini' // Currently hardcoded as only Gemini supports it
  };

  const requestOptions = { ...defaultOptions, ...options };

  try {
    const response = await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.research}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        description: description,
        temperature: requestOptions.temperature,
        max_tokens: requestOptions.maxTokens,
        provider: requestOptions.provider
      }),
    });

    if (!response.ok) {
      // Check for unauthorized response
      if (clearApiKeyIfUnauthorized(response.status)) {
        throw new Error('API key unauthorized. Please enter a valid API key.');
      }
      
      const errorData = await response.json();
      console.error("Research API Error Data:", errorData);
      throw new Error(`Research API error: ${errorData.error || response.statusText}`);
    }

    // Parse the response which should contain result and timestamp
    const data = await response.json();

    // Ensure the response has the expected structure
    if (typeof data.result === 'undefined') { // Check for undefined specifically
      throw new Error('Invalid response format: missing result');
    }

    return {
      result: data.result,
      timestamp: data.timestamp
    };
  } catch (error) {
    console.error('Error sending research request to Diagram Assistant:', error);
    throw error; // Re-throw the error to be caught by the component
  }
}

export default {
  sendChatMessage,
  checkApiHealth,
  extractMermaidCode,
  escapeMermaidText,
  processMermaidCode,
  MAX_HISTORY_MESSAGES,
  getPersonalizedPrompts,
  sendResearchRequest
}; 